import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useRevenueOpportunityDismissals } from '~/hooks/useRevenueOpportunityDismissals/useRevenueOpportunityDismissals';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { opportunityRoute } from '~/utils/revenueOpportunities/opportunityDestination';
import { dismissChoices } from '~/utils/revenueOpportunities/opportunityDismissals';
import { canManageRevenueOpportunityPreferences } from '~/utils/revenueOpportunities/revenueOpportunityAccess';
import { SEE_ALL_URL_STATE, seeAllFiltersOf, seeAllUrlChange } from '~/utils/revenueOpportunities/seeAllLink';
import { kindOptions, listQuery, pageWithin, phoneFilterConfig, phoneFiltersOf, phoneSortOptions, serviceOptions, shownOpportunityKeys, singleCard, SORT_COLUMNS, tableStateOf, toRevenueOpportunityRows, unrecognisedRowsReport } from '../../utils/revenueOpportunityRows';
import { useRevenueOpportunitiesList } from './hooks/useRevenueOpportunitiesList';
const RANK_SORT = {
    name: 'rank',
    direction: 'asc'
};
export const useRevenueOpportunitiesPage = (toastTestIds)=>{
    var _ref, _ref1, _query_offset, _ref2, _ref3, _ref4;
    const navigate = useNavigate();
    const { browser } = useIoContext();
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { paginationPageSize, inputDebounceTime } = useAppConfig();
    const isVendor = useStore(selectors.user.isVendor);
    const role = useStore(selectors.user.role);
    const { urlState, changeUrlState } = useStateFromUrl(SEE_ALL_URL_STATE);
    const { sort, changeSort: changeUrlSort } = useSortFromUrl({
        defaultSort: undefined,
        sortIds: SORT_COLUMNS
    });
    const [page, setPage] = useState(1);
    const { search: urlSearch, kind: urlKinds, service: urlServices } = urlState;
    const { search, kinds, serviceKeys } = useMemo(()=>seeAllFiltersOf({
            search: urlSearch,
            kind: urlKinds,
            service: urlServices
        }), [
        urlSearch,
        urlKinds,
        urlServices
    ]);
    const query = useMemo(()=>listQuery({
            search,
            kinds,
            serviceKeys,
            sort,
            page,
            pageSize: paginationPageSize
        }), [
        search,
        kinds,
        serviceKeys,
        sort,
        page
    ]);
    const { list, isShowingPreviousAnswer, isLoading, isFetching, error, refresh, readListState } = useRevenueOpportunitiesList(query, {
        refreshOnHookMount: true
    });
    const skippedReport = unrecognisedRowsReport((_ref = list === null || list === void 0 ? void 0 : list.rows) !== null && _ref !== void 0 ? _ref : []);
    useEffect(()=>{
        if (skippedReport != null) {
            browser.reportError(new Error(skippedReport));
        }
    }, [
        skippedReport
    ]);
    const shownPage = pageWithin(page, list === null || list === void 0 ? void 0 : list.total, paginationPageSize);
    useEffect(()=>{
        if (shownPage !== page) {
            setPage(shownPage);
        }
    }, [
        shownPage,
        page
    ]);
    const { isDismissing, dismissCard } = useRevenueOpportunityDismissals(toastTestIds, {
        identities: shownOpportunityKeys(list),
        isFetching,
        read: ()=>{
            const latest = readListState();
            return {
                identities: shownOpportunityKeys(latest.list),
                isFetching: latest.isFetching
            };
        }
    });
    const rows = toRevenueOpportunityRows((_ref1 = list === null || list === void 0 ? void 0 : list.rows) !== null && _ref1 !== void 0 ? _ref1 : [], texts, (_query_offset = query.offset) !== null && _query_offset !== void 0 ? _query_offset : 0);
    const lastSyncedAt = (_ref2 = list === null || list === void 0 ? void 0 : list.lastSyncedAt) !== null && _ref2 !== void 0 ? _ref2 : null;
    const kindChoices = kindOptions(texts);
    const serviceChoices = serviceOptions((_ref3 = list === null || list === void 0 ? void 0 : list.services) !== null && _ref3 !== void 0 ? _ref3 : []);
    const phoneSelection = phoneFiltersOf(kinds, serviceKeys);
    const changeListUrl = (next)=>{
        setPage(1);
        changeUrlState(next);
    };
    const changeSort = (param)=>{
        let { name, direction } = param;
        setPage(1);
        changeUrlSort({
            colIndex: SORT_COLUMNS.findIndex((column)=>column === name),
            sort: direction
        });
    };
    const applyPhoneFilters = function applyPhoneFilters(next) {
        let nextSort = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : sort;
        var _next_kind, _next_service;
        changeListUrl(seeAllUrlChange({
            kinds: (_next_kind = next.kind) !== null && _next_kind !== void 0 ? _next_kind : [],
            serviceKeys: (_next_service = next.service) !== null && _next_service !== void 0 ? _next_service : []
        }));
        changeUrlSort(nextSort);
    };
    return {
        filters: {
            search,
            searchDebounceMs: inputDebounceTime,
            kinds,
            serviceKeys,
            kindOptions: kindChoices,
            serviceOptions: serviceChoices,
            changeSearch: (nextSearch)=>changeListUrl({
                    search: nextSearch
                }),
            changeKinds: (nextKinds)=>changeListUrl(seeAllUrlChange({
                    kinds: nextKinds
                })),
            changeServices: (nextServiceKeys)=>changeListUrl(seeAllUrlChange({
                    serviceKeys: nextServiceKeys
                })),
            phoneSelection,
            phoneSort: sort,
            phoneLabels: {
                kind: texts.seeAll.filters.type(),
                service: texts.seeAll.filters.service()
            },
            phoneConfig: phoneFilterConfig(kindChoices, serviceChoices),
            phoneSortOptions: phoneSortOptions({
                client: texts.seeAll.columns.client(),
                type: texts.seeAll.columns.type(),
                service: texts.seeAll.columns.service(),
                confidence: texts.seeAll.columns.confidence(),
                value: texts.seeAll.columns.yearlyValue()
            }),
            setPhoneFilters: (next)=>applyPhoneFilters(typeof next === 'function' ? next(phoneSelection) : next),
            applyPhoneFilters
        },
        rows,
        rowActions: {
            act: (row)=>navigate(opportunityRoute(row.opportunity)),
            dismissChoices: (row)=>dismissChoices(singleCard(row), texts),
            dismiss: (row, option)=>void dismissCard(singleCard(row), option, row.biData),
            isDismissing
        },
        lastSyncedDate: lastSyncedAt == null ? null : timeModel.formatDate(lastSyncedAt, {
            long: true
        }),
        tableState: tableStateOf({
            isLoading: isLoading && list == null || isShowingPreviousAnswer,
            rowCount: rows.length
        }),
        hasError: error != null && list == null,
        canEditPreferences: canManageRevenueOpportunityPreferences({
            isVendor,
            role
        }),
        tableSort: sort == null ? RANK_SORT : {
            name: SORT_COLUMNS[sort.colIndex],
            direction: sort.sort
        },
        pagination: {
            page,
            pageSize: paginationPageSize,
            total: (_ref4 = list === null || list === void 0 ? void 0 : list.total) !== null && _ref4 !== void 0 ? _ref4 : 0,
            onPageChange: setPage
        },
        changeSort,
        retry: ()=>void refresh({
                throwOnError: false
            }),
        goBack: ()=>navigate(routes.vendorDashboard),
        openPreferences: ()=>navigate(routes.revenueOpportunityPreferences)
    };
};
